import AuthModal, {AuthModalState, AuthModalType} from '@gaia/components/AuthModal';
import theme from '@gaia/theme';
import AuthModalContext from '@map/contexts/AuthModalContext';
import {Map, type MapProps} from '@map/Map';
import {useSetHighlightLocation} from '@map/Map/store/highlightLocation';
import {useSetUpsellModalState} from '@map/popups/Upsell/store';
import UpsellModal from '@map/popups/Upsell/UpsellModal';
import {StyledEngineProvider, ThemeProvider} from '@mui/material/styles';
import {withLDProvider} from 'launchdarkly-react-client-sdk';
import {Suspense, useCallback, useEffect, useState} from 'react';
import ReactDOM from 'react-dom';
import {GoogleReCaptchaProvider} from 'react-google-recaptcha-v3';
import {useSyncExternalStore} from 'use-sync-external-store/shim';

// Implemented in `detail.html`
declare const store: {
    get: () => [number, number] | undefined;
    set: (value: [number, number] | undefined) => void;
    subscribe: (callback: () => void) => () => void;
};

export function render(props: MapProps, element: ReactDOM.Container) {
    ReactDOM.render(
        <LaunchDarklyProvider>
            <Suspense fallback={null}>
                <App {...props} />
            </Suspense>
        </LaunchDarklyProvider>,
        element
    );
}

const LaunchDarklyProvider = withLDProvider({
    clientSideID: process.env.LAUNCHDARKLY_CLIENT_SIDE_ID!,
    user: {
        anonymous: true,
        key: process.env.LAUNCHDARKLY_ANONYMOUS_USER_KEY,
    },
})(({children}) => <>{children}</>);

/**
 * This component serves to synchronize state between the global store that is set by Angular code
 * and the Jotai atom used in React.
 *
 * The point is to encapsulate the re-renders caused by the state changes in a nested component
 * within the <App>, because the <App> itself does not need to re-render when the state changes,
 * and the point of putting the `highlightLocation` state in a Jotai atom is to avoid re-renders
 * of the entire React tree.
 */
function SyncHighlightLocation() {
    // Get `highlightLocation` from store defined inline in `detail.html`, which is set by Rickshaw, and
    // sync it to the Jotai atom used in React.
    const highlightLocation = useSyncExternalStore(store.subscribe, store.get);
    const setHighlightLocation = useSetHighlightLocation();
    useEffect(() => {
        setHighlightLocation(highlightLocation);
    }, [highlightLocation, setHighlightLocation]);

    return null;
}

function App(props: MapProps) {
    // The entrypoint of the app contains the state required for manipulating the modals that appear over the entire app.
    // There are two such modals:
    //   - AuthModal: allows the user to login or create an account
    //   - UpsellModal: presents onboarding upsell as well as contextual paywalls to the user
    const [authModal, setAuthModal] = useState<AuthModalState>({
        type: undefined,
        analyticsContext: undefined,
        onUserAuthenticated: (form) => {
            if (form === 'register') {
                setUpsellModalState({selectedScreen: undefined});
            }
        },
    });

    const setAuthModalType = useCallback(
        (type: AuthModalType | undefined) => {
            setAuthModal({...authModal, type});
        },
        [setAuthModal, authModal]
    );

    const setUpsellModalState = useSetUpsellModalState();

    // The AuthModalContext provides this function instead of the raw SetState function from the setState hook.
    // contextSetAuthModal wraps the onUserAuthenticated callback to also include a trigger for displaying
    //   the user onboarding upsell modal.
    const contextSetAuthModal = useCallback(
        (authModalState: AuthModalState) => {
            setAuthModal({
                ...authModalState,
                onUserAuthenticated: (form) => {
                    if (form === 'register') {
                        setUpsellModalState({selectedScreen: undefined});
                    }
                    authModalState.onUserAuthenticated?.();
                },
            });
        },
        [setUpsellModalState]
    );

    return (
        <GoogleReCaptchaProvider reCaptchaKey={process.env.RECAPTCHA_SITE_KEY!}>
            <AuthModalContext.Provider value={contextSetAuthModal}>
                <StyledEngineProvider injectFirst>
                    <ThemeProvider theme={theme}>
                        <SyncHighlightLocation />
                        <Map {...props} />
                        <AuthModal setAuthModalType={setAuthModalType} {...authModal} />
                        {!props.isEmbedded && <UpsellModal />}
                    </ThemeProvider>
                </StyledEngineProvider>
            </AuthModalContext.Provider>
        </GoogleReCaptchaProvider>
    );
}
